import { useEffect, useState, useCallback, useMemo, useRef, type JSX } from 'react'
import { useSearchParams, useNavigate } from 'react-router'
import { FileCheck, Send, ClipboardCheck, ChevronDown, Check, CheckCircle2, XCircle, Clock } from 'lucide-react'
import ConversationInbox, {
  normalizeConversationStatus,
  type ConversationItem,
  type InboxScope,
} from '../../../components/conversations/ConversationInbox.js'
import { api } from '../../../api/client.js'
import { formatCustomerName, cleanIdentifier } from '../../../utils/formatCustomerName.js'

/**
 * Unified Approvals screen — three audience tabs:
 *
 *   • For My Approval — requests awaiting *my* review (unreviewed only)
 *   • Requested       — requests *I* sent, with approved/rejected/pending counts
 *   • All Approvals   — every approval request in the tenant, with counts
 *
 * Both old routes redirect here with ?tab= so existing links keep working.
 * An approval-state filter dropdown (?state=) lets the user filter by
 * approved / rejected / pending within any tab.
 */

type ApprovalTab = 'for-my-approval' | 'requested' | 'all-approvals'
type ApprovalStateFilter = 'all' | 'approved' | 'rejected' | 'pending'

interface ApprovalRow {
  id: string
  referenceId: string
  subject: string | null
  channelOrigin: string
  status: string
  createdAt: string
  isRead: boolean
  isStarred: boolean
  dateSent: string
  reviewed: boolean
  dateReviewed: string | null
  reviewRemark: string | null
  senderRemark: string | null
  levelName: string | null
  stateName: string | null
  senderName: string | null
  approverName: string | null
  assignedAgentName: string | null
  displayName: string | null
  primaryHandle: string | null
  reviewedCount?: number
  approvedCount?: number
  rejectedCount?: number
  totalRequests?: number
  // Classification labels resolved server-side (same set as GET /conversations)
  priorityLabel: string | null
  typeLabel: string | null
  stateLabel: string | null
  urgencyLabel: string | null
  impactLabel: string | null
  categoryLabel: string | null
  subcategoryLabel: string | null
  locationLabel: string | null
  branchLabel: string | null
  departmentLabel: string | null
  campaignLabel: string | null
  teamLabel: string | null
  firstResponseDueAt: string | null
  resolutionDueAt: string | null
  lastModifiedAt: string | null
  lastModifiedBy: string | null
}

function mapPriority(label: string | null): ConversationItem['priority'] {
  if (!label) return undefined
  const l = label.toLowerCase()
  if (/urgent|critical|p1/.test(l)) return 'urgent'
  if (/high|p2/.test(l)) return 'high'
  if (/medium|normal|p3/.test(l)) return 'medium'
  if (/low|p4/.test(l)) return 'low'
  return undefined
}

/** Shared classification/label columns → ConversationItem (null → undefined). */
function classification(row: ApprovalRow) {
  return {
    priority: mapPriority(row.priorityLabel),
    typeLabel: row.typeLabel ?? undefined,
    stateLabel: row.stateLabel ?? undefined,
    urgencyLabel: row.urgencyLabel ?? undefined,
    impactLabel: row.impactLabel ?? undefined,
    categoryLabel: row.categoryLabel ?? undefined,
    subcategoryLabel: row.subcategoryLabel ?? undefined,
    locationLabel: row.locationLabel ?? undefined,
    branchLabel: row.branchLabel ?? undefined,
    departmentLabel: row.departmentLabel ?? undefined,
    campaignLabel: row.campaignLabel ?? undefined,
    teamLabel: row.teamLabel ?? undefined,
    firstResponseDueAt: row.firstResponseDueAt ?? undefined,
    resolutionDueAt: row.resolutionDueAt ?? undefined,
    lastModifiedAt: row.lastModifiedAt ?? undefined,
    lastModifiedBy: row.lastModifiedBy ?? undefined,
  }
}

/** Row → inbox item for the "For My Approval" tab. */
function toMyApprovalItem(row: ApprovalRow): ConversationItem {
  const tags: string[] = []
  if (row.reviewed) {
    // State names are tenant-defined free text (e.g. "Settlement Approval",
    // "Un-Justified Complain Approved", "Settlement Rejected"). Match the word
    // stems /reject/ and /approv/ so "Approval"/"Approved"/"Approve" all count
    // as approved — the narrower /approved/ missed "…Approval". Reject is
    // checked first so a name can never be mis-tagged as approved.
    if (row.stateName && /reject/i.test(row.stateName)) tags.push('Rejected')
    else if (row.stateName && /approv/i.test(row.stateName)) tags.push('Approved')
    else tags.push('Reviewed')
  } else {
    tags.push('Pending')
  }

  return {
    id: row.id,
    customerName: formatCustomerName(row.displayName, row.primaryHandle, row.channelOrigin),
    customerHandle: row.primaryHandle ? cleanIdentifier(row.primaryHandle) : undefined,
    customerAvatar: undefined,
    subject: row.subject ?? `(Level: ${row.levelName ?? '—'} · State: ${row.stateName ?? '—'})`,
    preview: row.reviewed ? row.reviewRemark ?? '' : row.senderRemark ?? '',
    channel: (row.channelOrigin as ConversationItem['channel']) ?? 'email',
    status: normalizeConversationStatus(row.status),
    slaStatus: undefined,
    isRead: row.isRead,
    isStarred: row.isStarred,
    isArchived: false,
    timestamp: row.reviewed ? (row.dateReviewed ?? row.dateSent) : row.dateSent,
    createdAt: row.createdAt,
    slaStartedAt: undefined,
    assignedTo: row.senderName ?? undefined,
    interactionId: row.referenceId,
    source: undefined,
    tags,
    csatScore: null,
    transferCount: 0,
    ...classification(row),
    ...(row.reviewedCount !== undefined ? { reviewedCount: row.reviewedCount } : {}),
    ...(row.approvedCount !== undefined ? { approvedCount: row.approvedCount } : {}),
    ...(row.rejectedCount !== undefined ? { rejectedCount: row.rejectedCount } : {}),
    ...(row.totalRequests !== undefined ? { totalApprovalRequests: row.totalRequests } : {}),
  }
}

/** Row → inbox item for the "Requested" tab (carries review outcome tags/counts). */
function toRequestedItem(row: ApprovalRow): ConversationItem {
  const tags: string[] = []
  if (row.reviewed) {
    // State names are tenant-defined free text (e.g. "Settlement Approval",
    // "Un-Justified Complain Approved", "Settlement Rejected"). Match the word
    // stems /reject/ and /approv/ so "Approval"/"Approved"/"Approve" all count
    // as approved — the narrower /approved/ missed "…Approval". Reject is
    // checked first so a name can never be mis-tagged as approved.
    if (row.stateName && /reject/i.test(row.stateName)) tags.push('Rejected')
    else if (row.stateName && /approv/i.test(row.stateName)) tags.push('Approved')
    else tags.push('Reviewed')
  } else {
    tags.push('Pending')
  }

  return {
    id: row.id,
    customerName: formatCustomerName(row.displayName, row.primaryHandle, row.channelOrigin),
    customerHandle: row.primaryHandle ? cleanIdentifier(row.primaryHandle) : undefined,
    customerAvatar: undefined,
    subject: row.subject ?? `(Level: ${row.levelName ?? '—'} · State: ${row.stateName ?? '—'})`,
    preview: row.reviewed ? row.reviewRemark ?? '' : row.senderRemark ?? '',
    channel: (row.channelOrigin as ConversationItem['channel']) ?? 'email',
    status: normalizeConversationStatus(row.status),
    slaStatus: undefined,
    isRead: row.isRead,
    isStarred: row.isStarred,
    isArchived: false,
    timestamp: row.reviewed ? (row.dateReviewed ?? row.dateSent) : row.dateSent,
    createdAt: row.createdAt,
    slaStartedAt: undefined,
    assignedTo: row.assignedAgentName ?? undefined,
    interactionId: row.referenceId,
    source: undefined,
    tags,
    csatScore: null,
    transferCount: 0,
    ...classification(row),
    ...(row.reviewedCount !== undefined ? { reviewedCount: row.reviewedCount } : {}),
    ...(row.approvedCount !== undefined ? { approvedCount: row.approvedCount } : {}),
    ...(row.rejectedCount !== undefined ? { rejectedCount: row.rejectedCount } : {}),
    ...(row.totalRequests !== undefined ? { totalApprovalRequests: row.totalRequests } : {}),
  }
}

/** Row → inbox item for the "All Approvals" tab — same as Requested but also
 *  carries the approver name so the Agent column can show who reviewed. */
function toAllApprovalItem(row: ApprovalRow): ConversationItem {
  const tags: string[] = []
  if (row.reviewed) {
    // State names are tenant-defined free text (e.g. "Settlement Approval",
    // "Un-Justified Complain Approved", "Settlement Rejected"). Match the word
    // stems /reject/ and /approv/ so "Approval"/"Approved"/"Approve" all count
    // as approved — the narrower /approved/ missed "…Approval". Reject is
    // checked first so a name can never be mis-tagged as approved.
    if (row.stateName && /reject/i.test(row.stateName)) tags.push('Rejected')
    else if (row.stateName && /approv/i.test(row.stateName)) tags.push('Approved')
    else tags.push('Reviewed')
  } else {
    tags.push('Pending')
  }

  return {
    id: row.id,
    customerName: formatCustomerName(row.displayName, row.primaryHandle, row.channelOrigin),
    customerHandle: row.primaryHandle ? cleanIdentifier(row.primaryHandle) : undefined,
    customerAvatar: undefined,
    subject: row.subject ?? `(Level: ${row.levelName ?? '—'} · State: ${row.stateName ?? '—'})`,
    preview: row.reviewed ? row.reviewRemark ?? '' : row.senderRemark ?? '',
    channel: (row.channelOrigin as ConversationItem['channel']) ?? 'email',
    status: normalizeConversationStatus(row.status),
    slaStatus: undefined,
    isRead: row.isRead,
    isStarred: row.isStarred,
    isArchived: false,
    timestamp: row.reviewed ? (row.dateReviewed ?? row.dateSent) : row.dateSent,
    createdAt: row.createdAt,
    slaStartedAt: undefined,
    assignedTo: row.approverName ?? row.assignedAgentName ?? undefined,
    interactionId: row.referenceId,
    source: undefined,
    tags,
    csatScore: null,
    transferCount: 0,
    ...classification(row),
    ...(row.reviewedCount !== undefined ? { reviewedCount: row.reviewedCount } : {}),
    ...(row.approvedCount !== undefined ? { approvedCount: row.approvedCount } : {}),
    ...(row.rejectedCount !== undefined ? { rejectedCount: row.rejectedCount } : {}),
    ...(row.totalRequests !== undefined ? { totalApprovalRequests: row.totalRequests } : {}),
  }
}

/**
 * Collapse rows that resolve to the same conversation. Each tab keys its list
 * items by conversation id, so a conversation that carries several approval requests
 * must appear only once — otherwise the same row renders repeatedly. The API
 * already de-dupes, but this keeps the list correct even if a response (or a
 * cached one) still carries per-request rows.
 */
function dedupeById(items: ConversationItem[]): ConversationItem[] {
  const seen = new Set<string>()
  const out: ConversationItem[] = []
  for (const item of items) {
    if (seen.has(item.id)) continue
    seen.add(item.id)
    out.push(item)
  }
  return out
}

function parseTab(v: string | null): ApprovalTab {
  if (v === 'requested') return 'requested'
  if (v === 'all-approvals') return 'all-approvals'
  return 'for-my-approval'
}

function parseStateFilter(v: string | null): ApprovalStateFilter {
  if (v === 'approved' || v === 'rejected' || v === 'pending') return v
  return 'all'
}

/** Check whether a row matches the given approval state filter. */
function rowMatchesState(row: ConversationItem, state: ApprovalStateFilter): boolean {
  if (state === 'all') return true
  if (state === 'pending') return row.tags?.includes('Pending') ?? false
  if (state === 'approved') return row.tags?.includes('Approved') ?? false
  if (state === 'rejected') return row.tags?.includes('Rejected') ?? false
  return true
}

export default function ApprovalsPage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams()
  const tab = parseTab(searchParams.get('tab'))
  const stateFilter = parseStateFilter(searchParams.get('state'))

  const [myApprovals, setMyApprovals] = useState<ConversationItem[]>([])
  const [requested, setRequested] = useState<ConversationItem[]>([])
  const [allApprovals, setAllApprovals] = useState<ConversationItem[]>([])
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  // Approval state filter dropdown
  const [stateMenuOpen, setStateMenuOpen] = useState(false)
  const stateMenuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!stateMenuOpen) return
    const handler = (e: MouseEvent): void => {
      if (stateMenuRef.current && !stateMenuRef.current.contains(e.target as Node)) {
        setStateMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [stateMenuOpen])

  // All three lists are fetched together so each tab's badge count is always
  // accurate regardless of which tab is active.
  const fetchAll = useCallback(async () => {
    setLoading(true)
    const [mine, sent, all] = await Promise.allSettled([
      api.get<{ conversations: ApprovalRow[] }>('/conversations/my-approvals'),
      api.get<{ conversations: ApprovalRow[] }>('/conversations/requested-approvals'),
      api.get<{ conversations: ApprovalRow[] }>('/conversations/all-approvals'),
    ])
    setMyApprovals(
      mine.status === 'fulfilled'
        ? dedupeById((mine.value.conversations ?? []).map(toMyApprovalItem))
        : [],
    )
    setRequested(
      sent.status === 'fulfilled'
        ? dedupeById((sent.value.conversations ?? []).map(toRequestedItem))
        : [],
    )
    setAllApprovals(
      all.status === 'fulfilled'
        ? dedupeById((all.value.conversations ?? []).map(toAllApprovalItem))
        : [],
    )
    setLoading(false)
  }, [])

  useEffect(() => {
    void fetchAll()
  }, [fetchAll])

  const setTab = (next: ApprovalTab): void => {
    const params = new URLSearchParams(searchParams)
    if (next === 'for-my-approval') params.delete('tab')
    else params.set('tab', next)
    setSearchParams(params, { replace: true })
  }

  const setStateFilter = (next: ApprovalStateFilter): void => {
    const params = new URLSearchParams(searchParams)
    if (next === 'all') params.delete('state')
    else params.set('state', next)
    setSearchParams(params, { replace: true })
  }

  // Compute summary counts for the subtitle based on the active tab's data
  // (before the state filter is applied, so the subtitle shows totals).
  const activeList = tab === 'requested' ? requested : tab === 'all-approvals' ? allApprovals : myApprovals
  const summary = useMemo(() => {
    let approved = 0, rejected = 0, pending = 0
    for (const c of activeList) {
      if (c.tags?.includes('Approved')) approved++
      else if (c.tags?.includes('Rejected')) rejected++
      else if (c.tags?.includes('Pending')) pending++
    }
    return { approved, rejected, pending }
  }, [activeList])

  // Apply the state filter to the active tab's list
  const conversations = useMemo(
    () => activeList.filter((c) => rowMatchesState(c, stateFilter)),
    [activeList, stateFilter],
  )

  const scopes: InboxScope[] = [
    { key: 'for-my-approval', label: 'For My Approval', icon: <FileCheck className="h-3.5 w-3.5" />, count: myApprovals.length },
    { key: 'requested', label: 'Requested', icon: <Send className="h-3.5 w-3.5" />, count: requested.length },
    { key: 'all-approvals', label: 'All Approvals', icon: <ClipboardCheck className="h-3.5 w-3.5" />, count: allApprovals.length },
  ]

  const subtitle = `${summary.approved} approved · ${summary.rejected} rejected · ${summary.pending} pending`

  // Approval state filter dropdown — rendered in the toolbar via extraToolbarContent
  const STATE_FILTERS: { key: ApprovalStateFilter; label: string; icon: JSX.Element }[] = [
    { key: 'all', label: 'All States', icon: <ClipboardCheck className="h-4 w-4" /> },
    { key: 'pending', label: 'Pending', icon: <Clock className="h-4 w-4" /> },
    { key: 'approved', label: 'Approved', icon: <CheckCircle2 className="h-4 w-4" /> },
    { key: 'rejected', label: 'Rejected', icon: <XCircle className="h-4 w-4" /> },
  ]
  const activeStateMeta = STATE_FILTERS.find((f) => f.key === stateFilter) ?? STATE_FILTERS[0]!

  const stateFilterDropdown = (
    <div ref={stateMenuRef} className="relative">
      <button
        onClick={() => setStateMenuOpen((v) => !v)}
        className={`flex h-8 items-center gap-2 rounded-[3px] border px-2.5 text-[12.5px] font-semibold transition-colors hover:border-slate-300 hover:text-slate-900 ${
          stateFilter !== 'all' ? 'border-[#C9D9FB] bg-blue-50 text-[#3366FF] hover:border-[#C9D9FB] hover:text-[#3366FF]' : 'border-slate-200 bg-white text-slate-600'
        }`}
      >
        <span className={stateFilter !== 'all' ? 'text-[#3366FF]' : 'text-slate-400'}>{activeStateMeta.icon}</span>
        {activeStateMeta.label}
        <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition-transform ${stateMenuOpen ? 'rotate-180' : ''}`} />
      </button>
      {stateMenuOpen && (
        <div className="absolute top-full left-0 z-40 mt-1.5 w-48 overflow-hidden rounded-[3px] border border-slate-200 bg-white py-1 shadow-xl shadow-black/5">
          {STATE_FILTERS.map((f) => {
            const on = stateFilter === f.key
            return (
              <button
                key={f.key}
                onClick={() => { setStateFilter(f.key); setStateMenuOpen(false) }}
                className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-[12.5px] font-medium transition-colors ${
                  on ? 'bg-blue-50 text-[#3366FF]' : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                <span className={on ? 'text-[#3366FF]' : 'text-slate-400'}>{f.icon}</span>
                <span className="flex-1">{f.label}</span>
                {on && <Check className="h-4 w-4" />}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )

  return (
    <>
      <ConversationInbox
        title="Approvals"
        subtitle={subtitle}
        scopes={scopes}
        activeScope={tab}
        onScopeChange={(next) => setTab(next as ApprovalTab)}
        conversations={conversations}
        loading={loading}
        onRefresh={() => void fetchAll()}
        onSelectConversation={(id) => {
          void api.put(`/agent/me/mark-read`, { conversationIds: [id] }).catch(() => {})
          const row = conversations.find((c) => c.id === id)
          navigate(`/agent/conversation/view/${id}`, { state: { tabLabel: row?.interactionId ? `#${row.interactionId}` : 'Conversation' } })
        }}
        extraToolbarContent={stateFilterDropdown}
      />
    </>
  )
}
